// Phase B — Welfare assignment workflow API client.
// Wraps /welfare-assign/* endpoints: allocation, approvals, inboxes.
import apiClient from '@/shared/api/client'

const BASE = '/welfare-assign'

// ─── Inbox row types ──────────────────────────────────────────────────

export interface RoomInboxRow {
  employee_id: string
  emp_code: string
  emp_name: string
  department_id: string | null
  category: string
  colony_id: string | null
  room_id: string | null
  bed_id: string | null
  allocation_start_date: string | null
  rent_amount: string | null
  security_deposit: string | null
  updated_at: string | null
}

export interface RationInboxRow {
  employee_id: string
  emp_code: string
  emp_name: string
  department_id: string | null
  category: string
  ration_allowance_rule_id: string | null
  ration_amount_override: string | null
  ration_effective_from: string | null
  ration_sanction_status: string | null
  ration_sanctioned_by_gm_at: string | null
  updated_at: string | null
}

export interface ActivationIssue {
  field: string
  severity: 'BLOCK' | 'WARN'
  message: string
}

export interface ActivationCheck {
  ok: boolean
  blocks: number
  warnings: number
  issues: ActivationIssue[]
}

// ─── API ──────────────────────────────────────────────────────────────

export const welfareAssignmentApi = {
  // Allocation mutations (used later when joining UI ships)
  allocateRoom: async (employeeId: string, body: Record<string, unknown>) =>
    (await apiClient.post(`${BASE}/employees/${employeeId}/room`, body)).data,
  approveRoom: async (employeeId: string) =>
    (await apiClient.post(`${BASE}/employees/${employeeId}/room/approve`)).data,
  rejectRoom: async (employeeId: string, reason: string) =>
    (await apiClient.post(`${BASE}/employees/${employeeId}/room/reject`, { reason })).data,
  vacateRoom: async (employeeId: string, vacation_date?: string) =>
    (await apiClient.post(`${BASE}/employees/${employeeId}/room/vacate`,
      { vacation_date: vacation_date ?? null })).data,

  assignCanteen: async (employeeId: string, body: Record<string, unknown>) =>
    (await apiClient.post(`${BASE}/employees/${employeeId}/canteen`, body)).data,

  requestRation: async (employeeId: string, body: Record<string, unknown>) =>
    (await apiClient.post(`${BASE}/employees/${employeeId}/ration/request`, body)).data,
  sanctionRationGm: async (employeeId: string) =>
    (await apiClient.post(`${BASE}/employees/${employeeId}/ration/sanction-gm`)).data,
  sanctionRationHr: async (employeeId: string) =>
    (await apiClient.post(`${BASE}/employees/${employeeId}/ration/sanction-hr`)).data,
  rejectRation: async (employeeId: string, by: 'GM' | 'HR', reason: string) =>
    (await apiClient.post(`${BASE}/employees/${employeeId}/ration/reject`,
      { by, reason })).data,

  // Activation checklist (for joining UI later)
  activationCheck: async (employeeId: string) =>
    (await apiClient.get<ActivationCheck>(
      `${BASE}/employees/${employeeId}/activation-check`)).data,

  // Inboxes
  inboxRooms: async (plant_id?: string) =>
    (await apiClient.get<RoomInboxRow[]>(`${BASE}/inbox/rooms`,
      { params: { plant_id } })).data,
  inboxRationGm: async (plant_id?: string) =>
    (await apiClient.get<RationInboxRow[]>(`${BASE}/inbox/ration/gm`,
      { params: { plant_id } })).data,
  inboxRationHr: async (plant_id?: string) =>
    (await apiClient.get<RationInboxRow[]>(`${BASE}/inbox/ration/hr`,
      { params: { plant_id } })).data,
}
